import { DateTime } from 'luxon';
import React, { HTMLProps, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Bond } from '../../../../types/Bond';
import { Asset } from '../../../../types/Asset';
import { Stock } from '../../../../types/Stock';
import { toPrice, toPercentage } from '../../../../utils/parsers';

import { StickyDisclosure } from '../../../general/Disclosure';
import { Portfolio } from '../../../../types/Portfolio';
import { Tooltip } from '../../../general/Tooltip';
import { TooltipIndicator } from '../../../general/TooltipIndicator';
import { FundsSelectionContext } from '../../../../contexts/funds-robot/FundsSelectionContext';

const DetailTable: React.FC<{} & HTMLProps<HTMLDivElement>> = ({ className, children }) => {
    return <div className={`grid grid-cols-5 p-4 text-base leading-8 px-4 ${className}`}>{children}</div>;
};

const DetailRow: React.FC<{
    title: string;
    value: string | React.ReactNode;
    valueAlignment?: 'left' | 'right' | 'center';
}> = ({ title, value, valueAlignment = 'right' }) => {
    return (
        <>
            <div className="border-b col-span-3 border-gray-300 flex flex-row items-center text-sm lg:text-base">
                {title}
            </div>
            <div
                className={`border-b col-span-2 border-gray-300 flex items-center text-sm lg:text-base ${
                    valueAlignment === 'left'
                        ? 'text-left'
                        : valueAlignment === 'right'
                        ? 'text-right'
                        : valueAlignment === 'center'
                        ? 'text-center'
                        : 'text-right'
                }`}
            >
                <div className="flex-1 leading-normal">{value}</div>
            </div>
        </>
    );
};

const DetailRowHistoricalReturns: React.FC<{
    title: string;
    values: string[];
    isHeader?: boolean;
}> = ({ title, values, isHeader = false }) => {
    return (
        <>
            <div className={`border-b pr-2 col-span-2 border-gray-300 ${isHeader ? 'font-medium text-sm' : ''}`}>
                {title}
            </div>
            <div
                className={`col-span-3 flex flex-row border-b border-gray-300 text-left  ${
                    isHeader ? 'font-medium text-sm' : ''
                }`}
            >
                {values.map((value, index) => {
                    return (
                        <div
                            key={index + value + 'key'}
                            className={`flex-1 ${isHeader ? 'pr-2 py-1 text-left leading-snug' : ''}`}
                        >
                            {value}
                        </div>
                    );
                })}
            </div>
        </>
    );
};

export const ComparisonStickyCardDetailsStrategy: React.FC<{ asset: Asset } & HTMLProps<HTMLDivElement>> = ({
    asset,
    className,
}) => {
    return <p className={`px-4 py-1 pt-2 text-sm ${className}`}>{asset.investmentStrategyText}</p>;
};

export const ComparisonStickyCardDetailsBasicCharacteristics: React.FC<
    { asset: Asset } & HTMLProps<HTMLDivElement>
> = ({ asset, className }) => {
    const { t } = useTranslation('funds');
    return (
        <DetailTable className={className}>
            {asset?.referenceDateCharacteristics && (
                <DetailRow
                    title={t('funds-comparison-screen-reference-date')}
                    value={DateTime.fromISO(asset?.referenceDateCharacteristics).setLocale('nl').toFormat('d MMM yyyy')}
                />
            )}
            <DetailRow
                title={t('funds-comparison-screen-fund-costs-per-year')}
                value={toPercentage(asset?.basicCharacteristics.managementFee * 100)}
            />
            {asset?.basicCharacteristics.totalAssets && (
                <DetailRow
                    title={t('funds-comparison-screen-fund-size')}
                    value={toPrice(asset?.basicCharacteristics.totalAssets * 100, {
                        onlyFullEuros: true,
                        withCurrencySymbol: true,
                    })}
                />
            )}
            {asset?.basicCharacteristics.startDate && (
                <DetailRow
                    title={t('funds-comparison-screen-start-date')}
                    value={DateTime.fromISO(asset?.basicCharacteristics.startDate)
                        .setLocale('nl-NL')
                        .toFormat('d MMMM yyyy')}
                />
            )}
            {asset?.basicCharacteristics.isin && (
                <DetailRow title={t('funds-comparison-screen-isin')} value={asset?.basicCharacteristics.isin} />
            )}
            {asset?.basicCharacteristics.fundManager && (
                <DetailRow
                    title={t('funds-comparison-screen-fund-manager')}
                    value={asset?.basicCharacteristics.fundManager}
                />
            )}
            {asset?.basicCharacteristics.portfolioManager && asset.category !== 'Portfolio' && (
                <DetailRow
                    title={t('funds-comparison-screen-fund-manager')}
                    value={asset?.basicCharacteristics.portfolioManager}
                />
            )}
            {asset?.basicCharacteristics.portfolioManager && asset.category === 'Portfolio' && (
                <DetailRow
                    title={t('funds-comparison-screen-fund-manager-portfolio')}
                    value={asset?.basicCharacteristics.portfolioManager}
                />
            )}
            {asset?.basicCharacteristics.benchmark && (
                <DetailRow
                    title={t('funds-comparison-screen-benchmark')}
                    value={asset?.basicCharacteristics.benchmark || 'n.v.t.'}
                />
            )}
        </DetailTable>
    );
};

export const ComparisonStickyCardDetailsUnderlyingBonds: React.FC<
    { asset: Bond | Portfolio } & HTMLProps<HTMLDivElement>
> = ({ asset, className }) => {
    const { t } = useTranslation('funds');

    const specifics = asset.category === 'Bond' ? asset.specifics : asset.specifics.bonds;

    return (
        <DetailTable className={className}>
            {specifics !== undefined && (
                <>
                    {asset?.referenceDateCharacteristics && (
                        <DetailRow
                            title={t('funds-comparison-screen-reference-date')}
                            value={DateTime.fromISO(asset?.referenceDateCharacteristics)
                                .setLocale('nl')
                                .toFormat('d MMM yyyy')}
                        />
                    )}
                    <DetailRow
                        title={t('funds-comparison-screen-underlying-bonds-amount')}
                        value={specifics?.amountBonds.toLocaleString('nl-NL', {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                        })}
                    />
                    <DetailRow
                        title={t('funds-comparison-screen-underlying-bonds-yield-to-maturity')}
                        value={
                            <div className="flex flex-row justify-between items-center">
                                <Tooltip
                                    tooltipTitle={t('tooltip-bonds-effective-ytm-title')}
                                    tooltipText={t('tooltip-bonds-effective-ytm-text')}
                                >
                                    <TooltipIndicator>?</TooltipIndicator>
                                </Tooltip>
                                <div>{`${toPercentage(specifics.yieldToMaturity * 100)}`}</div>
                            </div>
                        }
                    />
                    <DetailRow
                        title={t('funds-comparison-screen-underlying-bonds-average-coupon-rate')}
                        value={
                            <div className="flex flex-row justify-between items-center flex-1">
                                <Tooltip
                                    tooltipTitle={t('tooltip-bonds-coupon-title')}
                                    tooltipText={t('tooltip-bonds-coupon-text')}
                                >
                                    <TooltipIndicator>?</TooltipIndicator>
                                </Tooltip>
                                <div>{toPercentage(specifics.averageCouponRate * 100)}</div>
                            </div>
                        }
                    />
                    <DetailRow
                        title={t('funds-comparison-screen-underlying-bonds-average-duration')}
                        value={(Math.round(specifics.averageDuration * 100) / 100).toLocaleString('nl-NL', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                        })}
                    />
                    <DetailRow
                        title={t('funds-comparison-screen-underlying-bonds-average-credit-quality')}
                        value={specifics.averageCreditQuality}
                    />
                    <DetailRow
                        title={t('funds-comparison-screen-underlying-bonds-average-weighted-duration')}
                        value={
                            <div className="flex flex-row justify-between items-center">
                                <Tooltip
                                    tooltipTitle={t('tooltip-bonds-duration-title')}
                                    tooltipText={t('tooltip-bonds-duration-text')}
                                >
                                    <TooltipIndicator>?</TooltipIndicator>
                                </Tooltip>
                                <div>
                                    {(Math.round(specifics.averageWeightedDuration * 100) / 100).toLocaleString(
                                        'nl-NL',
                                        {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                        },
                                    )}
                                </div>
                            </div>
                        }
                    />
                </>
            )}
        </DetailTable>
    );
};

export const ComparisonStickyCardDetailsUnderlyingStocks: React.FC<
    { asset: Stock | Portfolio } & HTMLProps<HTMLDivElement>
> = ({ asset, className }) => {
    const { t } = useTranslation('funds');

    const specifics = asset.category === 'Stock' ? asset.specifics : asset.specifics.stocks;

    return (
        <DetailTable className={className}>
            {specifics && (
                <>
                    {asset?.referenceDateCharacteristics && (
                        <DetailRow
                            title={t('funds-comparison-screen-reference-date')}
                            value={DateTime.fromISO(asset?.referenceDateCharacteristics)
                                .setLocale('nl')
                                .toFormat('d MMM yyyy')}
                        />
                    )}
                    <DetailRow
                        title={t('funds-comparison-screen-underlying-stocks-amount')}
                        value={specifics.amountStocks.toLocaleString('nl-NL')}
                    />
                    <DetailRow
                        title={t('funds-comparison-screen-underlying-stocks-average-market-capitalization')}
                        value={
                            <div className="flex flex-row justify-between items-center">
                                <Tooltip
                                    tooltipTitle={t('tooltip-stocks-market-capitalization-title')}
                                    tooltipText={t('tooltip-stocks-market-capitalization-text')}
                                >
                                    <TooltipIndicator>?</TooltipIndicator>
                                </Tooltip>
                                <div>
                                    {`${(Math.round(specifics.averageMarketCapitalization * 100) / 100).toLocaleString(
                                        'nl-NL',
                                        { minimumFractionDigits: 2, maximumFractionDigits: 2 },
                                    )}`}
                                </div>
                            </div>
                        }
                    />
                    <DetailRow
                        title={t('funds-comparison-screen-underlying-stocks-dividend')}
                        value={
                            <div className="flex flex-row justify-between items-center">
                                <Tooltip
                                    tooltipTitle={t('tooltip-stocks-dividend-title')}
                                    tooltipText={t('tooltip-stocks-dividend-text')}
                                >
                                    <TooltipIndicator>?</TooltipIndicator>
                                </Tooltip>
                                <div>{toPercentage(specifics.dividend * 100)}</div>
                            </div>
                        }
                    />
                    <DetailRow
                        title={t('funds-comparison-screen-underlying-stocks-rentability-own-assets')}
                        value={
                            <div className="flex flex-row justify-between items-center">
                                <Tooltip
                                    tooltipTitle={t('tooltip-stocks-profitability-title')}
                                    tooltipText={t('tooltip-stocks-profitability-text')}
                                >
                                    <TooltipIndicator>?</TooltipIndicator>
                                </Tooltip>
                                <div>{toPercentage(specifics.rentabilityOwnAssets * 100)}</div>
                            </div>
                        }
                    />
                    <DetailRow
                        title={t('funds-comparison-screen-underlying-stocks-rate-book-value-ratio')}
                        value={
                            <div className="flex flex-row justify-between items-center">
                                <Tooltip
                                    tooltipTitle={t('tooltip-stocks-rate-value-ratio-title')}
                                    tooltipText={t('tooltip-stocks-rate-value-ratio-text')}
                                >
                                    <TooltipIndicator>?</TooltipIndicator>
                                </Tooltip>
                                <div>
                                    {(Math.round(specifics.rateBookValueRatio * 100) / 100).toLocaleString('nl-NL', {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                    })}
                                </div>
                            </div>
                        }
                    />
                    <DetailRow
                        title={t('funds-comparison-screen-underlying-stocks-rate-profit-ratio')}
                        value={
                            <div className="flex flex-row justify-between items-center">
                                <Tooltip
                                    tooltipTitle={t('tooltip-stocks-rate-profit-ratio-title')}
                                    tooltipText={t('tooltip-stocks-rate-profit-ratio-text')}
                                >
                                    <TooltipIndicator>?</TooltipIndicator>
                                </Tooltip>
                                <div>
                                    {(Math.round(specifics.rateProfitRatio * 100) / 100).toLocaleString('nl-NL', {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                    })}
                                </div>
                            </div>
                        }
                    />
                </>
            )}
        </DetailTable>
    );
};

export const ComparisonStickyCardDetailsRiskCharacteristics: React.FC<{ asset: Asset } & HTMLProps<HTMLDivElement>> = ({
    asset,
    className,
}) => {
    const { t } = useTranslation('funds');
    return (
        <div className="flex flex-col">
            <DetailTable className={className}>
                {asset?.referenceDateCharacteristics && (
                    <DetailRow
                        title={t('funds-comparison-screen-reference-date')}
                        value={DateTime.fromISO(asset?.referenceDateCharacteristics)
                            .setLocale('nl')
                            .toFormat('d MMM yyyy')}
                    />
                )}
                <DetailRow
                    title={t('funds-comparison-screen-risk-standard-deviation', 'Standaarddeviatie 36M')}
                    value={
                        <div className="flex flex-row justify-between items-center">
                            <Tooltip
                                tooltipTitle={t('tooltip-risk-characteristics-stock-deviation-title')}
                                tooltipText={t('tooltip-risk-characteristics-stock-deviation-text')}
                            >
                                <TooltipIndicator>?</TooltipIndicator>
                            </Tooltip>
                            <div>{toPercentage(asset.riskCharacteristics.standardDeviation36M * 100)}</div>
                        </div>
                    }
                />
                <DetailRow
                    title={t('funds-comparison-screen-risk-sharpe-ratio', 'Sharperatio 36M')}
                    value={
                        <div className="flex flex-row justify-between items-center">
                            <Tooltip
                                tooltipTitle={t('tooltip-risk-characteristics-stock-sharpe-title')}
                                tooltipText={t('tooltip-risk-characteristics-stock-sharpe-text')}
                            >
                                <TooltipIndicator>?</TooltipIndicator>
                            </Tooltip>
                            <div>
                                {asset.riskCharacteristics.sharpeRatio36M.toLocaleString('nl-NL', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                })}
                            </div>
                        </div>
                    }
                />
                {asset.riskCharacteristics.beta36M && asset.category !== 'Bond' && (
                    <DetailRow
                        title={t('funds-comparison-screen-risk-beta', 'Beta 36M')}
                        value={
                            <div className="flex flex-row justify-between items-center">
                                <Tooltip
                                    tooltipTitle={t('tooltip-risk-characteristics-stock-beta-title')}
                                    tooltipText={t('tooltip-risk-characteristics-stock-beta-text')}
                                >
                                    <TooltipIndicator>?</TooltipIndicator>
                                </Tooltip>
                                <div>
                                    {(Math.round(asset.riskCharacteristics.beta36M * 100) / 100).toLocaleString(
                                        'nl-NL',
                                        {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                        },
                                    )}
                                </div>
                            </div>
                        }
                    />
                )}
                {asset.riskCharacteristics.rSquared36M && asset.category !== 'Bond' && (
                    <DetailRow
                        title={t('funds-comparison-screen-risk-r-squared', 'R Squared 36M')}
                        value={
                            <div className="flex flex-row justify-between items-center">
                                <Tooltip
                                    tooltipTitle={t('tooltip-risk-characteristics-stock-squared-title')}
                                    tooltipText={t('tooltip-risk-characteristics-stock-squared-text')}
                                >
                                    <TooltipIndicator>?</TooltipIndicator>
                                </Tooltip>
                                <div>{`${toPercentage(asset.riskCharacteristics.rSquared36M)}`}</div>
                            </div>
                        }
                    />
                )}
                {asset.riskCharacteristics.trackingError12M && asset.category !== 'Bond' && (
                    <DetailRow
                        title={t('funds-comparison-screen-risk-tracking-error', 'Afwijking 12M')}
                        value={
                            <div className="flex flex-row justify-between items-center">
                                <Tooltip
                                    tooltipTitle={t('tooltip-risk-characteristics-stock-tracking-error-title')}
                                    tooltipText={t('tooltip-risk-characteristics-stock-tracking-error-text')}
                                >
                                    <TooltipIndicator>?</TooltipIndicator>
                                </Tooltip>
                                <div>{`${toPercentage(asset.riskCharacteristics.trackingError12M * 100)}`}</div>
                            </div>
                        }
                    />
                )}
                {asset.riskCharacteristics.grossTrackingDifference12M && asset.category !== 'Bond' && (
                    <DetailRow
                        title={t('funds-comparison-screen-risk-gross-tracking-difference', 'Bruto verschil 12M')}
                        value={
                            <div className="flex flex-row justify-between items-center">
                                <Tooltip
                                    tooltipTitle={t('tooltip-risk-characteristics-stock-tracking-bruto-title')}
                                    tooltipText={t('tooltip-risk-characteristics-stock-tracking-bruto-text')}
                                >
                                    <TooltipIndicator>?</TooltipIndicator>
                                </Tooltip>
                                <div>
                                    {`${toPercentage(asset.riskCharacteristics.grossTrackingDifference12M * 100)}`}
                                </div>
                            </div>
                        }
                    />
                )}
                {asset.riskCharacteristics.netTrackingDifference12M && asset.category !== 'Bond' && (
                    <DetailRow
                        title={t('funds-comparison-screen-risk-net-tracking-difference', 'Netto verschil 12M')}
                        value={
                            <div className="flex flex-row justify-between items-center">
                                <Tooltip
                                    tooltipTitle={t('tooltip-risk-characteristics-stock-tracking-netto-title')}
                                    tooltipText={t('tooltip-risk-characteristics-stock-tracking-netto-text')}
                                >
                                    <TooltipIndicator>?</TooltipIndicator>
                                </Tooltip>
                                <div>{`${toPercentage(asset.riskCharacteristics.netTrackingDifference12M * 100)}`}</div>
                            </div>
                        }
                    />
                )}
                {asset.basicCharacteristics.totalAssets && asset.basicCharacteristics.notInvestedAssets && (
                    <DetailRow
                        title={t('funds-comparison-screen-risk-not-invested', 'Vermogen niet belegd')}
                        value={
                            <div className="flex flex-row justify-between items-center">
                                <Tooltip
                                    tooltipTitle={t('tooltip-risk-characteristics-stock-not-invested-title')}
                                    tooltipText={t('tooltip-risk-characteristics-stock-not-invested-text')}
                                >
                                    <TooltipIndicator>?</TooltipIndicator>
                                </Tooltip>
                                <div>
                                    {`${toPercentage(
                                        (asset.basicCharacteristics.notInvestedAssets /
                                            asset.basicCharacteristics.totalAssets) *
                                            100,
                                    )}`}
                                </div>
                            </div>
                        }
                    />
                )}
            </DetailTable>
        </div>
    );
};

export const ComparisonStickyCardDetailsHistoricalReturns: React.FC<{ asset: Asset } & HTMLProps<HTMLDivElement>> = ({
    asset,
    className,
    ...rest
}) => {
    const { t } = useTranslation('funds');
    const fundsSelectionContext = useContext(FundsSelectionContext);
    return (
        <div className="flex flex-col">
            {fundsSelectionContext.selectionType === 'Portfolio' && (
                <Tooltip
                    className="flex flex-row justify-end"
                    tooltipTitle={t('tooltip-historic-return-portfolio-title')}
                    tooltipText={t('tooltip-historic-return-portfolio-text')}
                >
                    <TooltipIndicator>?</TooltipIndicator>
                </Tooltip>
            )}
            {!(fundsSelectionContext.selectionType === 'Portfolio') && (
                <Tooltip
                    className="flex flex-row justify-end"
                    tooltipTitle={t('tooltip-historic-return-bond-stock-title')}
                    tooltipText={t('tooltip-historic-return-bond-stock-text')}
                >
                    <TooltipIndicator>?</TooltipIndicator>
                </Tooltip>
            )}
            <DetailTable className={className}>
                {asset?.referenceDateHistoricalReturn && (
                    <DetailRow
                        title={t('funds-comparison-screen-reference-date')}
                        value={DateTime.fromISO(asset?.referenceDateHistoricalReturn)
                            .setLocale('nl')
                            .toFormat('d MMM yyyy')}
                    />
                )}
                <DetailRowHistoricalReturns
                    isHeader={true}
                    title={''}
                    values={[
                        t('funds-comparison-screen-historical-returns-after-costs'),
                        asset.category !== 'Portfolio'
                            ? t('funds-comparison-screen-historical-returns-before-costs')
                            : '',
                        asset.category === 'Stock' ? t('funds-comparison-screen-historical-returns-benchmark') : '',
                    ]}
                />
                {asset.historicalReturn
                    .filter((r) => !r.year)
                    .map((r) => {
                        const title = r.year ? r.year + '' : r.description;
                        return (
                            <DetailRowHistoricalReturns
                                key={r.id}
                                title={title}
                                values={[
                                    toPercentage(r.afterCosts),
                                    asset.category !== 'Portfolio' ? toPercentage(r.beforeCosts) : '',
                                    asset.category === 'Stock' ? toPercentage(r.benchmark) : '',
                                ]}
                            />
                        );
                    })}
                {asset.historicalReturn
                    .filter((r) => !!r.year)
                    .sort((r1, r2) => {
                        return r1.year! > r2.year! ? -1 : r1.year! < r2.year! ? 1 : 0;
                    })
                    .map((r) => {
                        const title = r.year ? r.year + '' : r.description;
                        return (
                            <DetailRowHistoricalReturns
                                key={r.id + r.year}
                                title={title}
                                values={[
                                    toPercentage(r.afterCosts),
                                    asset.category !== 'Portfolio' ? toPercentage(r.beforeCosts) : '',
                                    asset.category === 'Stock' ? toPercentage(r.benchmark) : '',
                                ]}
                            />
                        );
                    })}
            </DetailTable>
        </div>
    );
};

export const ComparisonStickyCardDetailsHistoricalReturnsSmall: React.FC<
    { asset: Asset } & HTMLProps<HTMLDivElement>
> = ({ asset, className, ...rest }) => {
    const { t } = useTranslation('funds');
    return (
        <>
            <div className={`flex flex-col px-4 text-sm lg:text-base ${className}`}>
                {asset.historicalReturn
                    .filter((r) => !r.year)
                    .map((r, index) => {
                        const title = r.year ? r.year + '' : r.description;
                        return (
                            <React.Fragment key={index}>
                                <div className="flex flex-row mt-4 mb-2">
                                    <div className="flex-1 font-medium text-base">{title}</div>
                                </div>
                                <div className="flex flex-row pb-1 mb-1 border-b border-gray-300">
                                    <div className="flex-2">
                                        {t('funds-comparison-screen-historical-returns-after-costs')}
                                    </div>
                                    <div className="flex-1 text-right">{toPercentage(r.afterCosts)}</div>
                                </div>
                                {asset.category !== 'Portfolio' && (
                                    <div className="flex flex-row pb-1 mb-1 border-b border-gray-300">
                                        <div className="flex-2">
                                            {t('funds-comparison-screen-historical-returns-before-costs')}
                                        </div>
                                        <div className="flex-1 text-right">{toPercentage(r.beforeCosts)}</div>
                                    </div>
                                )}
                                {asset.category === 'Stock' && (
                                    <div className="flex flex-row pb-1 mb-1 border-b border-gray-300">
                                        <div className="flex-2">
                                            {t('funds-comparison-screen-historical-returns-benchmark')}
                                        </div>
                                        <div className="flex-1 text-right">{toPercentage(r.benchmark)}</div>
                                    </div>
                                )}
                            </React.Fragment>
                        );
                    })}
                {asset.historicalReturn
                    .filter((r) => !!r.year)
                    .sort((r1, r2) => {
                        return r1.year! > r2.year! ? -1 : r1.year! < r2.year! ? 1 : 0;
                    })
                    .map((r, index) => {
                        const title = r.year ? r.year + '' : r.description;
                        return (
                            <React.Fragment key={index}>
                                <div className="flex flex-row mt-4 mb-2">
                                    <div className="flex-1 font-medium text-base">{title}</div>
                                </div>
                                <div className="flex flex-row pb-1 mb-1 border-b border-gray-300">
                                    <div className="flex-2">
                                        {t('funds-comparison-screen-historical-returns-after-costs')}
                                    </div>
                                    <div className="flex-1 text-right">{toPercentage(r.afterCosts)}</div>
                                </div>
                                {asset.category !== 'Portfolio' && (
                                    <div className="flex flex-row pb-1 mb-1 border-b border-gray-300">
                                        <div className="flex-2">
                                            {t('funds-comparison-screen-historical-returns-before-costs')}
                                        </div>
                                        <div className="flex-1 text-right">{toPercentage(r.beforeCosts)}</div>
                                    </div>
                                )}
                                {asset.category === 'Stock' && (
                                    <div className="flex flex-row pb-1 mb-1 border-b border-gray-300">
                                        <div className="flex-2">
                                            {t('funds-comparison-screen-historical-returns-benchmark')}
                                        </div>
                                        <div className="flex-1 text-right">{toPercentage(r.benchmark)}</div>
                                    </div>
                                )}
                            </React.Fragment>
                        );
                    })}
            </div>
        </>
    );
};

export const ComparisonStickyCardDetailsDividendPayments: React.FC<
    { fund: Stock | Bond } & HTMLProps<HTMLDivElement>
> = ({ fund, className, ...rest }) => {
    const { t } = useTranslation('funds');

    if ((fund.dividendPayments || []).length === 0) {
        return <div className="p-4 text-gray-500 text-center">Geen dividend uitgekeerd.</div>;
    }

    return (
        <DetailTable>
            <div className="border-b col-span-2 border-gray-300 font-medium">
                {t('funds-comparison-screen-dividend-payments-date')}
            </div>
            <div className={`border-b col-span-3 border-gray-300 font-medium text-right`}>
                {t('funds-comparison-screen-dividend-payments-payment')}
            </div>
            {fund.dividendPayments?.map((p) => {
                return (
                    <DetailRow
                        key={p.date}
                        title={DateTime.fromISO(p.date).toFormat('dd-MM-yyyy')}
                        value={toPrice(p.amountPerParticipation)}
                    />
                );
            })}
        </DetailTable>
    );
};

export const ComparisonStickyCardDetails: React.FC<{ asset: Asset } & HTMLProps<HTMLDivElement>> = ({
    asset,
    className,
    ...rest
}) => {
    const { t } = useTranslation('funds');
    return (
        <div className={`${className}`} {...rest}>
            <StickyDisclosure disclosureTitle={t('funds-comparison-screen-investment-strategy')}>
                <ComparisonStickyCardDetailsStrategy asset={asset} />
            </StickyDisclosure>
            <StickyDisclosure disclosureTitle={t('funds-comparison-screen-core-properties')}>
                <ComparisonStickyCardDetailsBasicCharacteristics asset={asset} />
            </StickyDisclosure>
            {(asset.category === 'Bond' || asset.category === 'Portfolio') && (
                <StickyDisclosure disclosureTitle={t('funds-comparison-screen-underlying-bonds')}>
                    <ComparisonStickyCardDetailsUnderlyingBonds asset={asset} />
                </StickyDisclosure>
            )}
            {(asset.category === 'Stock' || asset.category === 'Portfolio') && (
                <StickyDisclosure disclosureTitle={t('funds-comparison-screen-underlying-stocks')}>
                    <ComparisonStickyCardDetailsUnderlyingStocks asset={asset} />
                </StickyDisclosure>
            )}
            <StickyDisclosure disclosureTitle={t('funds-comparison-screen-risk-characteristics')}>
                <ComparisonStickyCardDetailsRiskCharacteristics asset={asset} />
            </StickyDisclosure>

            <StickyDisclosure disclosureTitle={t('funds-comparison-screen-historical-returns')}>
                <ComparisonStickyCardDetailsHistoricalReturns className="" asset={asset} />
            </StickyDisclosure>
            {(asset.category === 'Bond' || asset.category === 'Stock') && (
                <StickyDisclosure disclosureTitle={t('funds-comparison-screen-dividend-payments')}>
                    <ComparisonStickyCardDetailsDividendPayments fund={asset} />
                </StickyDisclosure>
            )}
        </div>
    );
};
